/* eslint-disable max-len */
import React, { useCallback, useContext, useState, useEffect } from "react";
import { LoadingContext, RecurringPaymentInfoModal } from "gw-capability-policy-react";
import { TranslatorContext } from "@jutro/locale";
import _ from "lodash";
import PropTypes from "prop-types";
import { renderContentFromMetadata } from "@jutro/uiconfig";
import {
  CustomIconUtil,
  CustomPolicyTypeUtil,
  CustomFormatterUtil,
  CustomTooltipUtil,
} from "gw-portals-util-js";
import { CustomClaimCardComponent } from "gw-capability-claim-react";
import { useAuthentication } from "gw-digital-auth-react";
import { ClaimService } from "gw-capability-claim";
import appConfig from "app-config";
import { isCapabilityEnabled } from "gw-portals-config-js";
import { BreakpointTrackerContext } from "@jutro/layout";
import messages from "./CustomPolicyCardComponent.messages";
import styles from "./CustomPolicyCardComponent.module.scss";
import metadata from './CustomPolicyCardComponent.metadata.json5';
import { useHistory } from 'react-router-dom';

function CustomPolicyCardComponent(props) {
  const translator = useContext(TranslatorContext);
  const loader = useContext(LoadingContext);
  const { data: policyData, billingData } = props;
  const { authHeader } = useAuthentication();
  const history = useHistory();
  const [policyClaimData, setpolicyClaimData] = useState([]);
  const latestPolicyPeriod = _.first(policyData.periods);
  const { capabilitiesConfig } = appConfig;
  const [isOpen, setIsOpen] =  useState(false);
  const emptyString = "";
  const [apiErrorExists, setAPIErrorExists] = useState(false);
  const [showRecurModal, setShowRecurModal] = useState(false);
  const [recurringInfoPaymentDetails, setrecurringInfoPaymentDetails] = useState({ creditCardExpDate: '', cardName: '', lastModDate: '', paymentMethod: '', paymentDate: ''});
  const breakpoint = useContext(BreakpointTrackerContext);
  const isMobile =
    breakpoint === "phoneWide" ||
    breakpoint === "phone" ||
    breakpoint === "tablet";

  useEffect(() => {
    let claimPromise = Promise.resolve([]);
    if(isOpen && _.isEmpty(policyClaimData) && !apiErrorExists) {
      if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: "claim" })) {
        claimPromise = ClaimService.getClaimSummaries(
          { policyNumber: latestPolicyPeriod.policyId },
          {},
          authHeader
        );
      }
      Promise.all([
        claimPromise.then(setpolicyClaimData).catch((error) => {
          setAPIErrorExists(error.errorCode === "GW_ERROR");
        }),
      ]).finally(() => {
      });
    }
  }, [isOpen]);

  // To render the components based on the clicks 
  // and specific card to open
  useEffect(() => {
    if(props.cardToOpen === latestPolicyPeriod.policyId && !isOpen){
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [props.cardToOpen]);

  // Callback which executes on click of the card 
  // either to open or close
  const toggleCard = useCallback(() => {
     // Case to check if the card is currently opened 
    // and again clicked to close it
    if (isOpen) {
      // CLOSE card AND reset parent state
      setIsOpen(false);
      props.cardToOpenCallback(undefined);
    } else {
      // OPEN card via parent (single source of truth)
      props.cardToOpenCallback(latestPolicyPeriod.policyId);
    }
  }, [isOpen]);

  const expandCard = useCallback(() => {
  if (!isOpen) {
    props.cardToOpenCallback(latestPolicyPeriod.policyId);
  }
}, [isOpen]);


  // navigate to payment summary with the required data
  const navigateToPaymentScheduleAndHistoryPage = useCallback((activeTab) => () => {
    let invoiceStreamId;
    const {
      accountLevelBilling: accountLevelBillingFromApi = false,
      invoiceStreams: invoiceStreamsFromApi = []
    } = billingData;

    if (accountLevelBillingFromApi && invoiceStreamsFromApi.length === 1) {
      invoiceStreamId = invoiceStreamsFromApi[0].internalId;
    } else {
      invoiceStreamId = invoiceStreamsFromApi.find((invoice) => {
        return invoice.label === latestPolicyPeriod.policyId;
      }).internalId;
    }
    //1852420 - Payment Schedule and History page - Page refresh gives 'Something went wrong' error
    let paySummaryStateData = {
      invoiceStreamId: invoiceStreamId,
      activeTab: activeTab,
      policyData: policyData
    }
    if (sessionStorage.getItem('paySummaryState')) {
      sessionStorage.removeItem('paySummaryState');
    }
    sessionStorage.setItem('paySummaryState', JSON.stringify(paySummaryStateData));
    history.push({
      pathname: '/payment-summary',
      state: paySummaryStateData
    });
  });

  const getPolicyTypeIcon = (policyType) => {
    const formattedPolicyType = policyType.replace(/ +/g, emptyString);
    const icon = CustomIconUtil.getCustomPolicyTypeIcon(formattedPolicyType);
    return icon;
  };
  const doesAPIErrorExists = useCallback(() => {
    return apiErrorExists;
  }, [apiErrorExists]);

  const readValue = useCallback(
    (id, path) => {
      if (path === "producerAgency") {
        return _.get(
          policyData.producerContact,
          "producerCodeOfServiceAgency_Ext"
        );
      }
      return _.get(policyData, path);
    },
    [policyData]
  );

  const isPaymentPastDue = () => {
    return latestPolicyPeriod.isPaymentPastDue_Ext;
  };

  const isPolicyStatusInForce = () => {
    return latestPolicyPeriod.status === "In Force";
  };

  const isPolicyStatusScheduled = () => {
    return latestPolicyPeriod.status === "Scheduled";
  };

  const isPolicyAgencyBill = () => {
    return latestPolicyPeriod.billingMethod_Ext === "Agency Bill";
  };

  const isPolicyExpiredOrPermanentCanceled = () => {
    return (
      !isPolicyAgencyBill() &&
      (isPolicyStatusExpired() ||
        (isPolicyStatusCanceled() && !latestPolicyPeriod.canMakeAPayment_Ext))
    );
  };

  const isPolicyStatusExpired = () => {
    return latestPolicyPeriod.status === "Expired";
  };

  const isPolicyStatusCanceled = () => {
    return latestPolicyPeriod.status === "Canceled";
  };

  const getActiveClaims = () => {
    if (policyClaimData.items) {
      switch (policyClaimData.activeClaimsCount_Ext) {
        case 0:
          return (
            <p className={styles.claimsMessage}>
              {translator(messages.noActiveClaim)}
            </p>
          );
        case 1:
          return (
            <p className={styles.claimsMessage}>
              {" "}
              {translator(messages.youHave)} <b>1</b>{" "}
              {translator(messages.activeClaim)}{" "}
            </p>
          );
        default:
          return (
            <p className={styles.claimsMessage}>
              {" "}
              {translator(messages.youHave)}{" "}
              <b>{policyClaimData.activeClaimsCount_Ext}</b>{" "}
              {translator(messages.activeClaims)}
            </p>
          );
      }
    }
    return " ";
  };

  const hasClaims = () => {
    return policyClaimData.items && policyClaimData.items.length > 0;
  };

  const hasMultipleClaims = () => {
    return policyClaimData.items && policyClaimData.items.length > 1;
  };

  const isAmountZero = (policyAmount) => {
    if (policyAmount === undefined || policyAmount.amount === 0) {
      return true;
    }
    return false;
  };

  const getPolicyStatus = (policy) => {
    if (isPolicyStatusInForce(policy)) {
      return translator(messages.statusActive);
    }
    return policy.status;
  };

  const getClassName = () => {
    switch (getPolicyStatus(latestPolicyPeriod)) {
      case "Canceled":
        return "policy-status-canceled";
      case "Past Due":
        return "policy-status-past-due";
      case "Expired":
        return "policy-status-expired";
      default:
        return "policy-status-active";
    }
  };

  function showModal(){
    const requestedPayDetails = {
      creditCardExpDate: latestPolicyPeriod.creditCardExpirationDate_Ext ? latestPolicyPeriod.creditCardExpirationDate_Ext : "",
      cardName: latestPolicyPeriod.nameOnCard_Ext,
      lastModDate: latestPolicyPeriod.lastModifiedDate_Ext,
      paymentMethod: latestPolicyPeriod.paymentMethod_Ext,
      paymentDate: latestPolicyPeriod.dateOfPayment_Ext,
      routingNumber: latestPolicyPeriod.routingNumber_Ext ? latestPolicyPeriod.routingNumber_Ext : ""
    };
    setrecurringInfoPaymentDetails(requestedPayDetails);
    setShowRecurModal(true);
  }

  function hideModal (){
    setShowRecurModal(!showRecurModal);
  }

  const overrideProps = {

    gridDivision: {
      className: !isOpen ? 'policy-content' : 'policy-content-expand'
    },
    claimGridLayout:{
      className: !isOpen? 'claim-card-column' : 'claim-card-expand'
    },
    pastDueNotification: {
      className: isOpen && isPaymentPastDue() && isPolicyStatusInForce() && !isPolicyAgencyBill()
        ?  'past-due-notification' :'noDisplay'
    },
    canceledExpiredNotification: {
      className: isOpen && (isPolicyStatusCanceled() || isPolicyStatusExpired() ) ?  'policy-card-warning-notification' :'noDisplay'
    },
    agencyBillNotification: {
      className: isOpen && isPolicyAgencyBill() ?  'background-notification' :'noDisplay'
    },
    scheduledPoliciesNotification: {
      className: isOpen && !isPolicyAgencyBill() && policyData.periods.length > 1? 'background-notification' :'noDisplay'
    },
    makeAPaymentLinkMessage: {
      to: `/choose-payment/${latestPolicyPeriod.policyId}?policyType=${
        latestPolicyPeriod.policyType
        }&isHOFlex=${CustomPolicyTypeUtil.isHOFlex(latestPolicyPeriod)}`,
      className: "policy-card-make-a-payment-link"
    },
    makeAPaymentButton: {
      to: `/choose-payment/${latestPolicyPeriod.policyId}?policyType=${
        latestPolicyPeriod.policyType
        }&isHOFlex=${CustomPolicyTypeUtil.isHOFlex(latestPolicyPeriod)}`,
      className:
        (isPolicyAgencyBill() || isPolicyStatusExpired() ||
          (isPolicyStatusCanceled() && !latestPolicyPeriod.canMakeAPayment_Ext) )? "noDisplay" : "policy-card-view-policy-button",
    },
    policyType: {
      content: CustomPolicyTypeUtil.getCustomPolicyTitle(latestPolicyPeriod),
    },
    policyNumber: {
      content: translator(messages.policyIdValue, {
        id: latestPolicyPeriod.policyId,
      }),
    },
    policyInForceDateRange: {
      content: CustomFormatterUtil.formatDateLongRange(
        latestPolicyPeriod.effective,
        latestPolicyPeriod.expiration
      ),
    },
    ezActivationCode: {
      content: latestPolicyPeriod.activationCode_Ext,
    },
    policyStatus: {
      content:  getPolicyStatus(latestPolicyPeriod) == 'Canceled'? "Cancelled" :  getPolicyStatus(latestPolicyPeriod),
      className: "policy-card-policy-status " + getClassName(),
    },
    policyStatus2: {
      content: getPolicyStatus(latestPolicyPeriod) == 'Active' && isPaymentPastDue() ? "Past Due" : "",
      className: !isPolicyAgencyBill() && getPolicyStatus(latestPolicyPeriod) == 'Active' && isPaymentPastDue()? "policy-card-policy-status policy-status-past-due" : "noDisplay",
    },
    lastPaymentReceived: {
      visible: !isPolicyAgencyBill(),
      value: isAmountZero(latestPolicyPeriod.lastPaymentReceivedAmount_Ext)
        ? "-"
        : translator(messages.lastPaymentReceivedValue, {
          lastPaymentReceivedAmt: CustomFormatterUtil.formatAmount(
            latestPolicyPeriod.lastPaymentReceivedAmount_Ext.amount
          ),
          lastPaymentReceivedAmtDate:
            latestPolicyPeriod.lastPaymentReceivedDate_Ext !== null
              ? CustomFormatterUtil.formatAccompanyingDate(
                latestPolicyPeriod.lastPaymentReceivedDate_Ext
              )
              : emptyString,
        }),
    },
    remainingBalance: {
      visible:
        !isPolicyAgencyBill() &&
        (isPolicyStatusInForce() || isPolicyStatusScheduled()),
      value: CustomFormatterUtil.formatAmount(
        latestPolicyPeriod.remainingPremium_Ext.amount
      ),
    },
    currentPaymentDueContainer: {
      // visible: latestPolicyPeriod.canMakeAPayment_Ext,
    },
    currentPaymentDue: {
      value: isAmountZero(latestPolicyPeriod.currentPaymentDue_Ext)
        ? "-"
        : translator(messages.nextPaymentDueValue, {
          nextPaymentAmt: CustomFormatterUtil.formatAmount(
            latestPolicyPeriod.currentPaymentDue_Ext.amount
          ),
          nextPaymentAmtDate:
            latestPolicyPeriod.nextBillDueDate_Ext !== null
              ? CustomFormatterUtil.formatAccompanyingDate(
                latestPolicyPeriod.currentBillDueDate_Ext
              )
              : emptyString,
        }),
      className: "policy-card-input-data",
      visible:
        !isPolicyStatusCanceled() &&
        !isPolicyAgencyBill() &&
        !isPolicyStatusExpired(),
    },

    paymentPastDue: {
      value:
        isPaymentPastDue() && isPolicyStatusCanceled() && !isPolicyAgencyBill()
          ? CustomFormatterUtil.formatAmount(
            latestPolicyPeriod.pastDueAmount_Ext.amount
          )
          : emptyString,
      className: "policy-card-payment-overdue",
      visible: isPolicyStatusCanceled() && !isPolicyAgencyBill(),
    },
    totalPremium: {
      value: CustomFormatterUtil.formatAmount(
        latestPolicyPeriod.premium.amount
      ),
    },
    totalPremiumAgency: {
      visible: isPolicyAgencyBill(),
      value: CustomFormatterUtil.formatAmount(
        latestPolicyPeriod.premium.amount
      ),
    },
    policyBillingType: {
      value: latestPolicyPeriod.billingType_Ext,
      visible: !isPolicyAgencyBill()
    },
    policyPaymentPlan: {
      visible: !isPolicyAgencyBill(),
      value: latestPolicyPeriod.paymentPlan_Ext,
    },
    billingContact: {
      value: latestPolicyPeriod.billingContactName_Ext,
      visible: !isPolicyAgencyBill()
    },
    paymentMethod: {
      value: latestPolicyPeriod.paymentMethod_Ext,
      className:
        latestPolicyPeriod.billingType_Ext !== "Invoice"
          ? "policy-card-input-data"
          : "noDisplay",
    },
    paymentMethodContainer: {
      visible: !isPolicyAgencyBill(),
      className:
        getPolicyStatus(latestPolicyPeriod) == "Active" || getPolicyStatus(latestPolicyPeriod) == 'Scheduled'
          ? "policy-card-input-data"
          : "noDisplay",
    },
    viewRecurringPayment: {
      visible: latestPolicyPeriod.billingType_Ext !== "Invoice",
      onClick: showModal
    },
    policyBillingMethod: {
      visible: !isPolicyAgencyBill(),
      value: latestPolicyPeriod.billingMethod_Ext,
    },
    ViewPolicyDetails: {
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      href: `/account-policy-details/${latestPolicyPeriod.policyId}`,
    },
    //1781447 - Payment Schedule and History Page Optimization
    ViewPaymentHistory: {
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      onClick: navigateToPaymentScheduleAndHistoryPage('pHistoryTab'),
      visible: !isPolicyAgencyBill()
    },
    //1781447 - Payment Schedule and History Page Optimization
    ViewPaymentSchedule: {
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      onClick: navigateToPaymentScheduleAndHistoryPage('pScheduleTab'),
      visible: !isPolicyStatusCanceled() && !isPolicyStatusExpired() && !isPolicyAgencyBill(),
    },
    DownloadDocuments: {
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      target: "_blank",
      to: `/account-policy-details/${latestPolicyPeriod.policyId}#customPolicyDocumentCard`,
    },
    setUpEzPay: {
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      target: "_blank",
      href: appConfig.env.EZPAY_URL,
      visible: latestPolicyPeriod.billingType_Ext === "Invoice",
    },
    updateEzPay :{
      icon: "mi-keyboard-backspace",
      className: "policy-detail-action",
      target: "_blank",
      href: appConfig.env.EZPAY_URL,
      visible: latestPolicyPeriod.billingType_Ext === "Recurring Electronic",
    },
    ezPayContainer: {
      visible: getPolicyStatus(latestPolicyPeriod) !== "Canceled" && !isPolicyAgencyBill(),
    },
    customerServiceContainer: {
      visible:
        !(isPolicyStatusInForce() || isPolicyStatusScheduled()) &&
        !isPolicyAgencyBill(),
    },
    customerServiceDescription: {
      content: translator(messages.customerService),
    },
    customerServiceDescriptionPhoneNumber: {
      content: translator(messages.customerServicePhoneNumber),
    },
    customClaimHeader: {
      content: translator(messages.claimsHeader),
      className: hasMultipleClaims()
        ? "claim-header claim-minified-header"
        : "claim-header",
    },
    activeclaims: {
      content: getActiveClaims(),
      visible: hasClaims() && !doesAPIErrorExists(),
    },
    fileClaimButton: {
      icon: 'mi-keyboard-backspace',
      className: hasClaims() ? 'file-claim-link file-claim-minified-link ' : 'file-claim-link',
      href: appConfig.env.FNOL_URL,
      target: '_blank',
      visible: !doesAPIErrorExists()
    },
    noClaim: {
      content: translator(messages.noClaimHistory),
      visible: !doesAPIErrorExists(),
    },
    claimAPIError: {
      content: translator(messages.claimAPIErrorHeader),
      visible: doesAPIErrorExists(),
    },
    claimAPIErrorMessage: {
      content: translator(messages.claimAPIErrorDescription),
      visible: doesAPIErrorExists(),
    },
    lastestclaims: {
      content: translator(messages.latestClaimUpdates),
      visible: !doesAPIErrorExists() && hasClaims(),
    },
    trackClaim: {
      content: translator(messages.trackClaims),
      visible: !doesAPIErrorExists(),
    },
    claimCardComponentPanel: {
      data: policyClaimData.items
        ? policyClaimData.items.slice(0, 2)
        : policyClaimData.items,
    },
    cancellationDate: {
      value: isPolicyStatusCanceled()
        ? CustomFormatterUtil.formatDate(
          latestPolicyPeriod.cancellationEffectiveDate_Ext
        )
        : emptyString,
      className: isPolicyStatusCanceled() && !isPolicyAgencyBill()
        ? "policy-card-input-data"
        : "noDisplay",
    },
    permanentCancellationDate: {
      value: isPolicyStatusCanceled()
        ? CustomFormatterUtil.formatDate(
          latestPolicyPeriod.cancellationEffectiveDate_Ext
        )
        : emptyString,
      visible:
        isPolicyStatusCanceled() &&
        !latestPolicyPeriod.canMakeAPayment_Ext &&
        !isPolicyAgencyBill(),
    },
    permanentCancellationReason: {
      value: isPolicyStatusCanceled()
        ? latestPolicyPeriod.cancellationReason_Ext
        : emptyString,
      visible:
        isPolicyStatusCanceled() &&
        !latestPolicyPeriod.canMakeAPayment_Ext &&
        !isPolicyAgencyBill(),
    },
    cancellationReason: {
      value: isPolicyStatusCanceled()
        ? latestPolicyPeriod.cancellationReason_Ext
        : emptyString,
      className: isPolicyStatusCanceled() && !isPolicyAgencyBill()
        ? "policy-card-input-data"
        : "noDisplay",
    },
    expirationReason: {
      value: "Renewal not taken",
      className: isPolicyStatusExpired() && !isPolicyAgencyBill()
        ? "policy-card-input-data"
        : "noDisplay",
    },
    chatboticon: {
      src: CustomIconUtil.getLogoSrc("chatbot-green-icon.png"),
      visible: !doesAPIErrorExists(),
    },
    copyContentContainer: {
      // visible: !isMobile,
      content: latestPolicyPeriod.policyId
        ? CustomTooltipUtil.getTooltip(latestPolicyPeriod.policyId)
        : emptyString,
    },
    copyContentContainer2: {
      // visible: !isMobile,
      content: latestPolicyPeriod.activationCode_Ext
        ? CustomTooltipUtil.getTooltip(latestPolicyPeriod.activationCode_Ext)
        : emptyString,
    },
    toggleArrowImage: {
      src: CustomIconUtil.getLogoSrc("arrow-down.png"),
      className: isOpen? 'toggle-arrow-open': 'toggle-arrow',
      onClick: toggleCard,
    },
    policyCardContainer: {
      onClick: expandCard,
    },
    recurringInfoModal: {
      recurringInfoPaymentDetails: recurringInfoPaymentDetails,
      visible: showRecurModal,
      dismissModal: hideModal
    },
  };

  const resolvers = {
    resolveClassNameMap: styles,
    resolveValue: readValue,
    resolveComponentMap: { CustomClaimCardComponent, recurringModal: RecurringPaymentInfoModal },
  };

  return renderContentFromMetadata(
    metadata.componentContent,
    overrideProps,
    resolvers
  );
}

CustomPolicyCardComponent.propTypes = {
  data: PropTypes.shape({}).isRequired,
};

export default CustomPolicyCardComponent;